import { CancellationToken } from "../../../base/common/cancellation";
import { Event } from "../../../base/common/events";
import { IItemAccessor } from "../../../base/common/fuzzyScorer";
import { IMarkdownString } from "../../../base/common/htmlContent";
import { ResolvedKeybinding } from "../../../base/common/keybindings";
import { IDisposable } from "../../../base/common/lifecycle";
import { Schemas } from "../../../base/common/network";
import { URI } from "../../../base/common/uri";
import { createDecorator } from "../../instantiation/common/registry";
import { IQuickAccessOptions, IQuickAccessProviderRunOptions } from "./quickAccess";



/**
 * A button rendered on a quick pick item row (right-aligned, shown on
 * hover/active like in VS Code). Clicking a button does NOT accept the item.
 */
export interface IQuickInputButton {
	/** Icon name as understood by the UI icon registry (e.g. "edit", "delete"). */
	icon: string;
	tooltip: string;
}

/**
 * Fired by a quick pick when the user clicked a button of an item.
 */
export interface IQuickPickItemButtonEvent<T extends QuickPickItem = QuickPickItem> {
	item: T;
	button: IQuickInputButton;
	/** Index of the clicked button within `item.buttons`. */
	buttonIndex: number;
}

/**
 * Represents a separator in the quick pick list
 */
export interface IQuickPickSeparator {
	/**
	 * The type of the quick pick item. Used to distinguish between 'item' and 'separator'
	 */
	type: 'separator';
	id?: string;
	label?: string;
	description?: string;
	ariaLabel?: string;
	// buttons?: readonly IQuickInputButton[];
	tooltip?: string | IMarkdownString;
}

export type QuickPickItem = IQuickPickItem | IQuickPickSeparator;

/** Weak handle used to lazily render a live quick-pick thumbnail. */
export interface IQuickInputPreviewTarget {
	readonly key: string;
	readonly label?: string;
	deref(): object | undefined;
}

/**
 * Base properties for a quick pick and quick tree item.
 */
export interface IQuickItem {
	id?: string;
	/** Not for display but for fuzzy scoring */
    name?: string;
	/** The main label of the quick item */
	label: string;
	ariaLabel?: string;
	description?: string;
	/**
	 * Whether the item is displayed in italics.
	 */
	italic?: boolean;
	/**
	 * Whether the item is displayed with a strikethrough.
	 */
	strikethrough?: boolean;
	/**
	 * Icon to be assigned to the quick item container
	 * directly.
	 */
	icon?: string;
	// highlights?: IQuickItemHighlights;
	/**
	 * Optional buttons rendered right-aligned on the item row. Clicking a
	 * button fires `IQuickPick.onDidTriggerItemButton` and does NOT accept
	 * the item or close the picker.
	 */
	buttons?: ReadonlyArray<IQuickInputButton>;
	/**
	 * Used when we're in multi-select mode. Renders a disabled checkbox.
	 */
	disabled?: boolean;

	/**
	 * Whether the item is selectable. Defaults to true.
	 */
	selectable?: boolean;

	thumbnailUrl?: string;
	previewTarget?: IQuickInputPreviewTarget;

	tooltip?: string;
}
/**
 * Represents a quick pick item used in the quick pick UI.
 */
export interface IQuickPickItem extends IQuickItem {
	/**
	 * The type of the quick pick item. Used to distinguish between 'item' and 'separator'
	 */
	type?: 'item';
	/**
	 * The detail text of the quick pick item. Shown as the second line.
	 */
	detail?: string;
	/**
	 * The tooltip for the quick pick item.
	 */
	tooltip?: string;// | IMarkdownString;
	// highlights?: IQuickPickItemHighlights;
	/**
	 * Allows to show a keybinding next to the item to indicate
	 * how the item can be triggered outside of the picker using
	 * keyboard shortcut.
	 */
	keybinding?: ResolvedKeybinding;
	/**
	 * Whether the item is picked by default when the Quick Pick is shown.
	 */
	picked?: boolean;
	/**
	 * Whether the item is always shown in the Quick Pick regardless of filtering.
	 */
	alwaysShow?: boolean;
	/**
	 * Defaults to true with `IQuickPick.canSelectMany`, can be false to disable picks for a single item
	 */
	pickable?: boolean;
}

export interface IKeyMods {
	readonly ctrlCmd: boolean;
	readonly alt: boolean;
}

export const NO_KEY_MODS: IKeyMods = { ctrlCmd: false, alt: false };


export interface IQuickPickWillAcceptEvent {

	/**
	 * Allows to disable the default accept handling
	 * of the picker. If `veto` is called, the picker
	 * will not trigger the `onDidAccept` event.
	 */
	veto(): void;
}

export interface IQuickPickDidAcceptEvent {

	/**
	 * Signals if the picker item is to be accepted
	 * in the background while keeping the picker open.
	 */
	inBackground: boolean;
}


/**
 * Represents a quick input control that allows users to make selections or provide input quickly.
 */
export interface IQuickInput extends IDisposable {

	/**
	 * Shows the quick input.
	 */
	show(): void;

	/**
	 * Hides the quick input.
	 */
	hide(): void;

}

/**
 * Represents a quick pick control that allows the user to select an item from a list of options.
 */
export interface IQuickPick<T extends QuickPickItem> extends IQuickInput {

	readonly onDidHide: Event<void>;

	/**
	 * The current value of the quick pick input.
	 */
	value: string;

	/**
	 * An event that is fired when the value of the quick pick input changes.
	 */
	readonly onDidChangeValue: Event<string>;

	/**
	 * An event that is fired when the quick pick is about to accept the selected item.
	 */
	readonly onWillAccept: Event<IQuickPickWillAcceptEvent>;

	/**
	 * An event that is fired when the quick pick has accepted the selected item.
	 */
	readonly onDidAccept: Event<IQuickPickDidAcceptEvent>;

	readonly onWillDispose: Event<void>;

	/**
	 * An event that is fired when the user clicked a button of an item
	 * (see `IQuickItem.buttons`). Triggering a button does not accept the
	 * item and keeps the picker open.
	 *
	 * Deliberately not typed with `T`: that would make `IQuickPick`
	 * invariant in its item type and break assigning e.g.
	 * `IQuickPick<QuickPickItem>` where `IQuickPick<IQuickPickItem>` is
	 * expected. Consumers narrow `event.item` themselves.
	 */
	readonly onDidTriggerItemButton: Event<IQuickPickItemButtonEvent>;

	/**
	 * The items to be displayed in the quick pick.
	 */
	items: ReadonlyArray<T>;

	activeItems: T[];

	/**
	 * The key modifiers.
	 */
	readonly keyMods: IKeyMods;

	/**
	 * Indicates whether the quick input is busy. Renders a progress bar if true.
	 */
	busy: boolean;
}




export const IQuickInputService = createDecorator<IQuickInputService>('quickInputService');

export interface IQuickInputService {

	readonly _serviceBrand: undefined;

	readonly onDidChange: Event<void>;

	readonly onShow: Event<{ prefix?: string }>;

	readonly onHide: Event<void>;


	open(prefix?: string): void;
	hide(picker?: IQuickPick<any>): void;

	/**
	 * Open the quick access picker with the optional value prefilled.
	 */
	show<T extends IQuickPickItem>(picker: IQuickPick<T>, prefix: string, options?: IQuickAccessOptions): void;

	/**
	 * Same as `show()` but instead of executing the selected pick item,
	 * it will be returned. May return `undefined` in case no item was
	 * picked by the user.
	 */
	pick<T extends IQuickPickItem>(picker: IQuickPick<T>, prefix: string, options?: IQuickAccessOptions): Promise<IQuickPickItem[] | undefined>;

}


export type IQuickPickItemWithResource = IQuickPickItem & { resource?: URI };

export class QuickPickItemScorerAccessor implements IItemAccessor<IQuickPickItemWithResource> {

	constructor(private options?: { skipDescription?: boolean; skipPath?: boolean }) { }

	getItemName(item: IQuickPickItemWithResource): string | undefined {
		return item.name;
	}

	getItemLabel(entry: IQuickPickItemWithResource): string {
		return entry.label;
	}

	getItemDescription(entry: IQuickPickItemWithResource): string | undefined {
		if (this.options?.skipDescription) {
			return undefined;
		}

		return entry.description;
	}

	getItemPath(entry: IQuickPickItemWithResource): string | undefined {
		if (this.options?.skipPath) {
			return undefined;
		}

		if (entry.resource?.scheme === Schemas.file) {
			return entry.resource.fsPath;
		}

		return entry.resource?.path;
	}
}

export const quickPickItemScorerAccessor = new QuickPickItemScorerAccessor();
